import React from "react";
import styled from "styled-components";
import { i18n } from "../../services";
import { Progress } from "../../lib";
import { PageContents } from "../../models";

import Mode from "./Mode";
import SplitView from "./SplitView";
import MergedView from "./MergedView";
import { AxiosResponse } from "axios";

const Container = styled.div`
    display: flex;
    flex-direction: column;
`;

const Description = styled.div<{ info?: boolean }>`
    margin-top: 70px;
    text-align: center;
    font-size: 18px;
    padding: 15px 15px;
    color: ${(p) => (p.info ? "#31708f" : "#000")};
    border: 1px solid ${(p) => (p.info ? "#bcdff1" : "#ccc")};
    background-color: ${(p) => (p.info ? "#d9edf7" : "#efefef")};
`;

const COMPARE_SPLIT = "split";
const COMPARE_UNIFIED = "unified";

type Props = {
    doc_id: number;
    compare: {
        src: number;
        dst: number;
    };
};
type State = {
    mode: string;
    loaded: boolean;
    compare: {
        src: string;
        dst: string;
    };
};

export class Compare extends React.Component<Props, State> {
    timeout: ReturnType<typeof setInterval> | null = null;

    constructor(props: Props) {
        super(props);
        this.state = {
            mode: COMPARE_SPLIT,
            loaded: false,
            compare: {
                src: "",
                dst: "",
            },
        };
    }

    modeValues = [
        { value: COMPARE_SPLIT, label: "merge.split" },
        { value: COMPARE_UNIFIED, label: "merge.unified" },
    ];

    componentDidMount() {
        const { src, dst } = this.props.compare;
        this.fetch(src, dst);
    }

    componentWillReceiveProps(newProps: Props) {
        const { src, dst } = newProps.compare;
        this.fetch(src, dst);
    }

    componentWillUnmount() {
        if (this.timeout !== null) {
            clearTimeout(this.timeout!);
        }
    }

    async fetch(src: number, dst: number) {
        if (src === -1 || dst === -1) {
            return;
        }

        this.setState({
            loaded: false,
            compare: {
                src: "",
                dst: "",
            },
        });

        try {
            const { doc_id } = this.props;
            const params = { doc_id };
            const {
                data: srcHTML,
            }: AxiosResponse<string> = await PageContents.fetch(
                `contents/${src}`,
                params
            );
            const {
                data: dstHTML,
            }: AxiosResponse<string> = await PageContents.fetch(
                `contents/${dst}`,
                params
            );

            this.setState({
                loaded: true,
                compare: {
                    src: srcHTML,
                    dst: dstHTML,
                },
            });
        } catch (e) {
            console.warn(e);
        }
    }

    isFilledSrcDst() {
        const {
            compare: { src, dst },
        } = this.props;
        return src !== -1 && dst !== -1;
    }

    description() {
        if (this.isFilledSrcDst()) {
            return (
                <Description>
                    {i18n.get("merge.compareDescription")}
                </Description>
            );
        }
        return null;
    }

    setMode = (mode: string) => this.setState({ mode });
    mode = () => {
        if (!this.isFilledSrcDst()) {
            return null;
        }

        return (
            <Mode
                onChange={this.setMode}
                values={this.modeValues}
                selected={this.state.mode}
            />
        );
    };

    view() {
        if (!this.isFilledSrcDst()) {
            return null;
        }

        const {
            mode,
            compare: { src, dst },
        } = this.state;

        return mode === COMPARE_SPLIT ? (
            <SplitView src={src} dst={dst} />
        ) : (
            <MergedView src={src} dst={dst} />
        );
    }

    render() {
        if (this.isFilledSrcDst() && !this.state.loaded) {
            return <Progress />;
        }

        const {
            compare: { src, dst },
        } = this.props;
        const {
            compare: { src: srcContent, dst: dstContent },
        } = this.state;
        if (src !== -1 && dst !== -1 && srcContent === dstContent) {
            return (
                <Description info={true}>
                    {i18n.get("merge.bothAreEqual")}
                </Description>
            );
        }

        return (
            <Container>
                {this.description()}
                {this.mode()}
                {this.view()}
            </Container>
        );
    }
}
